export interface LessonStep {
    title: string;
    description: string;
    imageUrl: string;
}

export interface LessonContent {
    id: string;
    title: string;
    markdown: string;
    steps?: LessonStep[];
}

export const LEARN_CONTENTS: Record<string, LessonContent> = {
    'getting-started': {
        id: 'getting-started',
        title: 'Getting Started with Magnus Resolve',
        markdown: `
# Welcome to Resolve
Magnus Resolve is your field performance and incident management portal. It connects field data back to your design engineering team.

### Focus Areas:
*   **Incident Management**: Log and track field failures.
*   **8D Workflow**: Systematic root cause analysis.
*   **FMEA Loopback**: Close the loop between Service and Engineering.
        `,
        steps: [
            {
                title: 'The Resolve Dashboard',
                description: 'Explore the interactive dashboard showing real-time field performance metrics.',
                imageUrl: '/learn/magnus_resolve_dashboard_1773450709989.png'
            }
        ]
    },
    'incident-reporting': {
        id: 'incident-reporting',
        title: 'Capturing Field Incidents',
        markdown: `
# Effective Incident Capture
Detailed incident reporting is the foundation of high-quality reliability data.

### Vital Information:
*   **Symptom**: What did the customer see?
*   **Environment**: Temperature, humidity, load conditions.
*   **Metadata**: Serial numbers, lot codes, production dates.
        `,
        steps: [
            {
                title: 'New Incident Entry',
                description: 'Watch the rapid-entry animation as field symptoms are categorized by AI.',
                imageUrl: '/learn/magnus_resolve_dashboard_1773450709989.png'
            }
        ]
    },
    'resolution-sync': {
        id: 'resolution-sync',
        title: 'The FMEA Loopback',
        markdown: `
# Syncing with Engineering
When a root cause is confirmed in Resolve, you can notify the FMEA team to update their risk analysis.

### The Sync Process:
1. Complete RCA.
2. Click "Propose FMEA Update".
3. AutoGain AI maps the root cause to the corresponding Failure Mode.
        `,
        steps: [
            {
                title: 'Closing the Loop',
                description: 'Observe the automated hand-off animation between Service and Engineering teams.',
                imageUrl: '/learn/magnus_resolve_rca_tools_1773450739343.png'
            }
        ]
    },
    'fault-tree-analysis': {
        id: 'fault-tree-analysis',
        title: 'Fault Tree Architect',
        markdown: `
# Visual Fault Tree Analysis
Building complex logic models to determine the root causes of top-level system failures.

### Key features of Fault Tree Architect:
*   **AI Generation**: Prompt the agent to "Build a fault tree for brake system failure" to get a draft in seconds.
*   **Probability Calculation**: Enter base event probabilities to calculate system-level risk.
*   **Logic Gates**: Drag-and-drop AND/OR gates to model failure propagation.
        `,
        steps: [
            {
                title: 'Logic Building',
                description: 'Drag gates and events onto the canvas to map the failure hierarchy.',
                imageUrl: '/learn/magnus_resolve_rca_tools_1773450739343.png' // Placeholder
            }
        ]
    },
    'reliability-analytics': {
        id: 'reliability-analytics',
        title: 'Analytical Engineering Lab',
        markdown: `
# Reliability Analytics
Leveraging statistical models (Weibull, MTBF) to predict future failures based on field data.

### Analytics Capabilities:
*   **Weibull Analysis**: Upload field failure times to generate life-expectancy curves.
*   **Warranty Tracking**: Monitor claims vs. prediction to identify emerging issues.
*   **MTBF Calculation**: Automatically calculate Mean Time Between Failures for critical components.
        `,
        steps: [
            {
                title: 'Statistical Modeling',
                description: 'Input raw failure data to generate predictive reliability charts.',
                imageUrl: '/learn/magnus_resolve_dashboard_1773450709989.png'
            }
        ]
    }
};
